import { Deferred, Head, Link, router, usePage } from "@inertiajs/react";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useLocalStorage } from "@uidotdev/usehooks";
import { type ContextFn, format, type Locale } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { AnimatePresence, motion } from "framer-motion";
import {
	ActivityIcon,
	BanIcon,
	BanknoteIcon,
	CameraIcon,
	CctvIcon,
	Clock3Icon,
	CopyIcon,
	DownloadIcon,
	ExternalLinkIcon,
	EyeIcon,
	FileTextIcon,
	ListFilterIcon,
	MailIcon,
	MapPinIcon,
	MapPinned,
	MoreHorizontalIcon,
	NotebookPen,
	OctagonXIcon,
	PackageIcon,
	PlusIcon,
	SearchIcon,
	Stethoscope,
	TicketIcon,
	Trash2Icon,
	UserPenIcon,
	XIcon,
} from "lucide-react";
import {
	startTransition,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ApptViewChanger from "@/components/admin-portal/appointment/appt-view-changer";
import {
	ChatCopyMenu,
	ORDER_CHAT_COPY_TEMPLATES,
} from "@/components/admin-portal/appointment/chat-copy-menu";
import {
	type DeletableDocument,
	DeleteDocumentDialog,
	type DeleteDocumentTarget,
} from "@/components/admin-portal/appointment/delete-document-dialog";
import {
	type BlockReason,
	blockReason,
	DisabledReasonTooltip,
	deleteDocumentReason,
	firstReason,
	packageStoppedReason,
	ReconcileBonusMenuItem,
	useActionMenuRegroup,
	useCanReconcileBonus,
	useCanUpdateSoap,
} from "@/components/admin-portal/appointment/details/action-buttons";
import type { Package } from "@/components/admin-portal/appointment/feature-form";
import {
	BulkUpdateStatusForm,
	CancelAppointmentForm,
	ChangePackageForm,
	ChangeVoucherForm,
	EditMedicalRecordForm,
	EditSoapForm,
	FeedbackReminderDialog,
	RegenerateInvoiceForm,
	UpdateAddressHistoryForm,
	UpdatePaymentForm,
	UpdatePICForm,
	UpdateStatusForm,
	type VoucherOption,
} from "@/components/admin-portal/appointment/feature-form";
import {
	PicRoleBadge,
	picRoleRank,
} from "@/components/admin-portal/appointment/form/pic-slot-selectors";
import {
	MitraAssignmentReasonDialog,
	type MitraAssignmentReasonTarget,
} from "@/components/admin-portal/appointment/mitra-assignment-reason";
import {
	type OrderAddressChange,
	OrderAddressSection,
} from "@/components/admin-portal/appointment/order-address-section";
import {
	type CreatorType,
	orderBlockReasons,
} from "@/components/admin-portal/appointment/order-block-reasons";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import PasteWaFormButton from "@/components/admin-portal/appointment/paste-wa-form-button";
import {
	type OrderPayment,
	PaymentsPanel,
} from "@/components/admin-portal/appointment/payments-panel";
import {
	type RowTag,
	RowTags,
} from "@/components/admin-portal/appointment/row-tags";
import { SettlePaymentsPanel } from "@/components/admin-portal/appointment/settle-payments";
import {
	StopPackageDialog,
	type StopPackageTarget,
} from "@/components/admin-portal/appointment/stop-package-dialog";
import type { VisitAddressSnapshot } from "@/components/admin-portal/appointment/update-address-scope";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import { useDateContext } from "@/components/providers/date-provider";
import { CopyButton } from "@/components/shared/copy-button";
import CopyableValue from "@/components/shared/copyable-value";
import RecordStepper from "@/components/shared/record-stepper";
import {
	ResponsiveDialog,
	type ResponsiveDialogProps,
} from "@/components/shared/responsive-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatVisitLine } from "@/lib/appointments/format-visit-line";
import {
	findReasonLabel,
	type ReasonOption,
} from "@/lib/appointments/reason-label";
import { appointmentBadgeClass } from "@/lib/appointments/status-styles";
import {
	isTimePreference,
	timePreferenceShortWindow,
} from "@/lib/appointments/time-preference";
import i18n from "@/lib/i18n";
import {
	ORDER_STATUS_OPTIONS,
	ORDER_STATUS_STYLES,
	PAYMENT_STATUS_STYLES,
} from "@/lib/orders/status-styles";
import {
	cn,
	copyToClipboard,
	debounce,
	downloadFileFromUrl,
	formatCurrency,
	populateQueryParams,
} from "@/lib/utils";
import type { Admin } from "@/types/admin-portal/admin";
import type {
	Appointment,
	AppointmentStatuses,
	OrderPaymentStatuses,
} from "@/types/admin-portal/appointment";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderRow {
	id: string;
	bonusRoReconcilable?: boolean;
	mitraVisit?: boolean;
	creatorType: CreatorType;
	mitraVisits?: {
		appointmentId: string;
		visitNumber: number;
		reason: string | null;
	}[];
	registrationNumber: string;
	patientId: string;
	patientName: string | null;
	packageName: string | null;
	serviceName: string | null;
	numberOfVisit: number | null;
	totalAmount: number;
	discountAmount: number;
	voucherCode: string | null;
	pricePerVisit: number | null;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: string;
	status: string;
	// The payment row an admin still has to close — an unpaid order, or what a
	// package change left behind. Null once nothing is open.
	openPayment: {
		kind: "charge" | "refund";
		needsInvoice: boolean;
	} | null;
	invoiceNumber: string | null;
	invoiceUrl: string | null;
	createdAt: string;
	therapistId: string | null;
	firstAppointmentId: string | null;
	appointments: {
		id: Appointment["id"];
		visitNumber: Appointment["visitNumber"];
		totalPackageVisits: Appointment["totalPackageVisits"];
		status: Appointment["status"];
		hasSoap: boolean;
		hasEvidence: boolean;
		isFinalVisit: boolean;
		appointmentDateTime: string | null;
		preferredDate: string | null;
		timePreference: "PAGI" | "SIANG" | "SORE" | "MALAM" | null;
		therapistName: string | null;
		serviceCode: string | null;
		locationCity: string | null;
	}[];
	latitude: number | null;
	longitude: number | null;
}

interface OrderDetail {
	id: string;
	registrationNumber: string;
	// The AppointmentDraft this order came from. Not bookingDraftId below, which points at
	// the customer-web booking_drafts row — a different table.
	draftId: string | null;
	creatorType: CreatorType;
	bookingDraftId: string | null;
	patientId: string;
	packageId: number;
	packageBasePrice: number;
	subtotal: number;
	discountType: string | null;
	discountValue: number;
	discountAmount: number;
	voucherCode: string | null;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: string;
	invoiceNumber: string | null;
	invoiceUrl: string | null;
	invoiceDueDate: string | null;
	status: string;
	specialNotes: string | null;
	cancellationReason: string | null;
	cancelledAt: string | null;
	cancelledBy: string | null;
	completedAt: string | null;
	// Set by Stop Package. totalAmount is the lowered bill; originalTotalAmount what it was.
	stopReason: string | null;
	stopNotes: string | null;
	stoppedAt: string | null;
	stoppedByName: string | null;
	originalTotalAmount: number | null;
	createdAt: string;
	updatedAt: string;
	// The admins on this Order, each carrying the KPI stage it owns. `roles` is absent
	// while DRAFT_PIC_KPI is off or when the Order came from no Draft.
	pics: Array<{
		id: string;
		name: string;
		roles?: string[];
		user?: { email: string } | null;
	}>;
	patient: {
		id: string;
		name: string;
		gender: string;
		dateOfBirth: string;
	} | null;
	package: {
		id: number;
		name: string;
		numberOfVisit: number;
		totalPrice: number;
		currency: string;
	} | null;
	appointments: {
		id: string;
		registrationNumber: string;
		visitNumber: number;
		status: string;
		appointmentDateTime: string | null;
		preferredDate: string | null;
		timePreference: "PAGI" | "SIANG" | "SORE" | "MALAM" | null;
		therapistName: string | null;
		mitraAssignmentReason?: string | null;
		serviceName: string | null;
		serviceCode: string | null;
		locationCity: string | null;
		address: VisitAddressSnapshot | null;
	}[];
	addressChanges: OrderAddressChange[];
	payments: OrderPayment[];
}

interface OrdersPageProps {
	orders?: {
		data: OrderRow[];
		metadata: Metadata;
	};
	selectedOrder?: OrderDetail;
	selectedAppointment?: Appointment;
	mitraAssignmentReasons?: ReasonOption[];
	optionsData?: {
		admins: Admin[];
		statuses: {
			key: keyof typeof AppointmentStatuses;
			value: AppointmentStatuses;
		}[];
		paymentStatuses: {
			label: OrderPaymentStatuses;
			value: OrderPaymentStatuses;
		}[];
		servicePackages?: Package[];
		vouchers?: VoucherOption[];
		locations?: {
			id: number;
			city: string;
			state: string;
			country: string;
		}[];
	};
}

export interface OrdersGlobalPageProps
	extends BaseGlobalPageProps,
		OrdersPageProps {
	[key: string]: any;
}

const PREFERENCES_STORAGE_KEY = "appointment-orders-preferences";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(
	dateString: string,
	locale: Locale = idLocale,
	tzDate?: ContextFn<Date>,
): string {
	// `in: tzDate` renders in the configured timezone (Asia/Jakarta); critical for
	// date-only values (e.g. preferred_date) that would otherwise drift a day in
	// non-WIB browsers.
	return format(new Date(dateString), "dd MMM yyyy", { locale, in: tzDate });
}

function formatDateTime(
	dateString: string,
	locale: Locale = idLocale,
	timeFormatStr: string = "HH:mm",
	tzDate?: ContextFn<Date>,
): string {
	const finalTimeFormat = locale.code === "id" ? "HH:mm" : timeFormatStr;
	return format(new Date(dateString), `dd MMM yyyy, ${finalTimeFormat}`, {
		locale,
		in: tzDate,
	});
}

// SOAP and evidence across the whole order. Unlike a visit, an order is rarely all or
// nothing — the documents land one visit at a time — so the chip carries the count and
// borrows the table's own palette: emerald for done, amber for half-done, muted for none.
// Same three states the Docs filter asks the database for (Order#filter_by_document_coverage).
const DOCUMENT_CHIPS = [
	{ key: "hasSoap", label: "SOAP", Icon: FileTextIcon },
	{ key: "hasEvidence", label: "Evidence", Icon: CameraIcon },
] as const;

// Mirrors Order::UNDOCUMENTABLE_STATUSES — the chip and the filter have to count the same
// visits, or an order reading 3/3 here would still fail the "All Visits" filter.
const UNDOCUMENTABLE_STATUSES = ["cancelled", "refunded"];

const DocumentCoverageCell = ({ order }: { order: OrderRow }) => {
	const visits = (order.appointments ?? []).filter(
		(visit) => !UNDOCUMENTABLE_STATUSES.includes(visit.status),
	);

	return (
		<div className="flex items-center justify-center gap-1">
			{DOCUMENT_CHIPS.map(({ key, label, Icon }) => {
				const filed = visits.filter((visit) => visit[key]).length;
				const coverage =
					filed === 0 ? "none" : filed === visits.length ? "all" : "partial";
				// Every visit cancelled or refunded leaves nothing to count, so the chip
				// names its document instead of reading 0/0.
				const summary = visits.length
					? `${label}: ${filed} of ${visits.length} visit${visits.length === 1 ? "" : "s"}`
					: `${label}: no visit needs one`;

				return (
					<span
						key={key}
						title={summary}
						className={cn(
							"inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
							coverage === "all" &&
								"border-emerald-200 bg-emerald-100 text-emerald-700",
							coverage === "partial" &&
								"border-amber-200 bg-amber-100 text-amber-700",
							coverage === "none" && "border-border text-muted-foreground/40",
						)}
					>
						<Icon className="size-3 flex-shrink-0" aria-hidden="true" />
						{visits.length ? `${filed}/${visits.length}` : label.toUpperCase()}
						<span className="sr-only">{summary}</span>
					</span>
				);
			})}
		</div>
	);
};

// ─── Column Definitions ───────────────────────────────────────────────────────

const OrderActionsCell = ({
	row,
	onOpenDetail,
	onOpenFeedbackDialog,
	onOpenBulkStatus,
}: {
	row: Row<OrderRow>;
	onOpenDetail: (id: string) => void;
	onOpenFeedbackDialog: (orderId: string, registrationNumber: string) => void;
	onOpenBulkStatus: (order: OrderRow) => void;
}) => {
	const order = row.original;
	const { props: globalProps, url: pageURL } = usePage<OrdersGlobalPageProps>();
	const canUpdateSoap = useCanUpdateSoap();
	const canReconcileBonus = useCanReconcileBonus();
	const regroup = useActionMenuRegroup();
	const [deleting, setDeleting] = useState<DeleteDocumentTarget | null>(null);
	const [mitraReasonTarget, setMitraReasonTarget] =
		useState<MitraAssignmentReasonTarget | null>(null);
	const [stopTarget, setStopTarget] = useState<StopPackageTarget | null>(null);
	const { t: tappt } = useTranslation("appointments", {
		useSuspense: false,
	});

	// Each entry's reason for being closed, or null when open — see order-block-reasons.
	const blocked = useMemo(
		() => orderBlockReasons(order, canUpdateSoap),
		[order, canUpdateSoap],
	);

	const { locale, tzDate, timeFormatDateFns } = useDateContext();

	const handleCopy = useCallback(async (value: string, label: string) => {
		// copyToClipboard falls back to execCommand when the async Clipboard API
		// is unavailable (e.g. non-HTTPS origins on the LAN).
		const ok = await copyToClipboard(value);
		if (ok) {
			toast.success(`${label} copied to clipboard`);
		} else {
			toast.error(`Failed to copy ${label.toLowerCase()}`);
		}
	}, []);

	// Copies every visit of this order as one line each, e.g.
	// "Visit 1: Selasa, 28 Juli 2026 (16:00 WIB) - FT Intan Rahmawati".
	const onCopyVisits = useCallback(() => {
		if (!order.appointments?.length) {
			toast.error("No appointments to copy");
			return;
		}
		const text = order.appointments
			.map((appt) =>
				formatVisitLine(appt, {
					locale,
					tzDate,
					timeFormat: timeFormatDateFns,
					t: tappt,
				}),
			)
			.join("\n");
		handleCopy(text, "Visits schedule");
	}, [
		order.appointments,
		locale,
		tzDate,
		timeFormatDateFns,
		tappt,
		handleCopy,
	]);

	const onCopyRegNumber = useCallback(() => {
		handleCopy(order.registrationNumber, "Registration number");
	}, [order.registrationNumber, handleCopy]);

	const onCopyInvoiceNumber = useCallback(() => {
		if (!order.invoiceNumber) {
			toast.error("Invoice number unavailable");
			return;
		}
		handleCopy(order.invoiceNumber, "Invoice number");
	}, [order.invoiceNumber, handleCopy]);

	const onCopyInvoiceUrl = useCallback(() => {
		if (!order.invoiceUrl) {
			toast.error("Invoice URL unavailable");
			return;
		}
		handleCopy(order.invoiceUrl, "Invoice URL");
	}, [order.invoiceUrl, handleCopy]);

	const onCopyPatientId = useCallback(() => {
		if (!order.patientId) {
			toast.error("Patient ID unavailable");
			return;
		}
		handleCopy(order.patientId, "Patient ID");
	}, [order.patientId, handleCopy]);

	const onCopyTherapistId = useCallback(() => {
		if (!order.therapistId) {
			toast.error("Therapist ID unavailable");
			return;
		}
		handleCopy(order.therapistId, "Therapist ID");
	}, [order.therapistId, handleCopy]);

	// for download soap report
	const onDownloadSoapReport = useCallback(
		async (appointmentId: string, visitNumber: number, totalVisits: number) => {
			const loadingToast = toast.loading(
				`Preparing SOAP Visit ${visitNumber}/${totalVisits}...`,
			);
			try {
				const url = `/admin-portal/appointments/${appointmentId}/soap-report`;
				await downloadFileFromUrl(url, "soap-report.pdf");
				toast.success(`SOAP Visit ${visitNumber}/${totalVisits} downloaded`, {
					id: loadingToast,
				});
			} catch (error) {
				const message = `Failed to download SOAP report`;
				console.error(`${message}: ${error}`);
				toast.error(message, { id: loadingToast });
			}
		},
		[],
	);

	const onDownloadSoapFinal = useCallback(async () => {
		// Find the last appointment (highest visit number)
		if (!order.appointments || order.appointments.length === 0) {
			toast.error("No appointments found");
			return;
		}

		const lastAppointment = order.appointments.reduce((last, current) =>
			current.visitNumber > last.visitNumber ? current : last,
		);

		const loadingToast = toast.loading("Preparing Final SOAP Report...");
		try {
			const url = `/admin-portal/appointments/${lastAppointment.id}/soap-report-final`;
			await downloadFileFromUrl(url, "final-report.pdf");
			toast.success("Final SOAP Report downloaded", { id: loadingToast });
		} catch (error) {
			const message = `Failed to download Final SOAP report`;
			console.error(`${message}: ${error}`);
			toast.error(message, { id: loadingToast });
		}
	}, [order.appointments]);

	const onDownloadInvoice = useCallback(async () => {
		if (!order.firstAppointmentId) {
			toast.error("Appointment ID unavailable");
			return;
		}

		const loadingToast = toast.loading("Preparing Invoice...");
		try {
			const url = `/admin-portal/appointments/${order.firstAppointmentId}/invoice`;
			await downloadFileFromUrl(url, "invoice.pdf");
			toast.success("Invoice downloaded", { id: loadingToast });
		} catch (error) {
			const message = "Failed to download Invoice";
			console.error(`${message}: ${error}`);
			toast.error(message, { id: loadingToast });
		}
	}, [order.firstAppointmentId]);

	const onViewOnGoogleMaps = useCallback(() => {
		if (!order.latitude || !order.longitude) {
			toast.error("Location unavailable");
			return;
		}
		window.open(
			`https://maps.google.com/?q=${order.latitude},${order.longitude}`,
			"_blank",
		);
	}, [order.latitude, order.longitude]);

	// for open the update pic form
	const openUpdatePicForm = useCallback(() => {
		if (order.firstAppointmentId) {
			router.get(
				pageURL,
				{ update_pic: order.firstAppointmentId },
				{
					only: [
						"adminPortal",
						"flash",
						"errors",
						"selectedAppointment",
						"optionsData",
					],
					preserveScroll: true,
					preserveState: true,
					replace: true,
				},
			);
		} else {
			toast.error("Appointment ID unavailable");
		}
	}, [order.firstAppointmentId, pageURL]);

	// for open the update payment form
	const openUpdatePayment = useCallback(() => {
		const { fullUrl } = populateQueryParams(pageURL, {
			update_payment: order.id,
			selected_order: order.id,
		});
		startTransition(() => {
			router.get(
				fullUrl,
				{},
				{
					only: [
						"adminPortal",
						"flash",
						"errors",
						"selectedOrder",
						"optionsData",
					],
					preserveScroll: true,
					preserveState: true,
					replace: true,
				},
			);
		});
	}, [order.id, pageURL]);

	// for open the reschedule page
	const openReschedulePage = useCallback(
		(id: string) => {
			if (id) {
				router.visit(
					`${globalProps.adminPortal.router.adminPortal.appointment.index}/${id}/reschedule`,
				);
			} else {
				toast.error("Appointment ID unavailable");
			}
		},
		[globalProps.adminPortal.router.adminPortal.appointment.index],
	);

	// for open the bulk reschedule page
	const openBulkReschedulePage = useCallback(
		(orderId: string) => {
			if (orderId) {
				router.visit(
					`${globalProps.adminPortal.router.adminPortal.appointment.index}/orders/${orderId}/bulk-reschedule`,
				);
			} else {
				toast.error("Order ID unavailable");
			}
		},
		[globalProps.adminPortal.router.adminPortal.appointment.index],
	);

	// for open the update status page
	const openUpdateStatusPage = useCallback(
		(id: string) => {
			if (id) {
				router.get(
					pageURL,
					{ update_status: id },
					{
						only: [
							"adminPortal",
							"flash",
							"errors",
							"selectedAppointment",
							"optionsData",
						],
						preserveScroll: true,
						preserveState: true,
						replace: true,
					},
				);
			} else {
				toast.error("Appointment ID unavailable");
			}
		},
		[pageURL],
	);

	const openEditMedicalRecord = useCallback(
		(id: string) => {
			if (id) {
				router.get(
					pageURL,
					{ edit_medical_record: id },
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
						replace: true,
					},
				);
			} else {
				toast.error("Appointment ID unavailable");
			}
		},
		[pageURL],
	);

	const openEditSoap = useCallback(
		(id: string) => {
			if (id) {
				router.get(
					pageURL,
					{ edit_soap: id },
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
						replace: true,
					},
				);
			} else {
				toast.error("Appointment ID unavailable");
			}
		},
		[pageURL],
	);

	// for open the cancel appointment modal
	const openCancelPage = useCallback(
		(id: string) => {
			if (id) {
				router.get(
					pageURL,
					{ cancel: id },
					{
						only: ["adminPortal", "flash", "errors", "selectedAppointment"],
						preserveScroll: true,
						preserveState: true,
						replace: true,
					},
				);
			} else {
				toast.error("Appointment ID unavailable");
			}
		},
		[pageURL],
	);

	// for open the change package form
	const openChangePackage = useCallback(() => {
		const { fullUrl } = populateQueryParams(pageURL, {
			change_package: order.id,
		});
		startTransition(() => {
			router.get(
				fullUrl,
				{},
				{
					only: [
						"adminPortal",
						"flash",
						"errors",
						"selectedOrder",
						"optionsData",
					],
					preserveScroll: true,
					preserveState: true,
					replace: true,
				},
			);
		});
	}, [order.id, pageURL]);

	// for open the change voucher form
	const openChangeVoucher = useCallback(() => {
		const { fullUrl } = populateQueryParams(pageURL, {
			change_voucher: order.id,
		});
		startTransition(() => {
			router.get(
				fullUrl,
				{},
				{
					only: [
						"adminPortal",
						"flash",
						"errors",
						"selectedOrder",
						"optionsData",
					],
					preserveScroll: true,
					preserveState: true,
					replace: true,
				},
			);
		});
	}, [order.id, pageURL]);

	// for open the regenerate invoice form
	const openRegenerateInvoice = useCallback(() => {
		const { fullUrl } = populateQueryParams(pageURL, {
			regenerate_invoice: order.id,
		});
		startTransition(() => {
			router.get(
				fullUrl,
				{},
				{
					only: [
						"adminPortal",
						"flash",
						"errors",
						"selectedOrder",
						"optionsData",
					],
					preserveScroll: true,
					preserveState: true,
					replace: true,
				},
			);
		});
	}, [order.id, pageURL]);

	// One "Visit n/N" entry per visit for a per-visit submenu: its gate decides both the
	// disabled state and the tooltip saying why, so the two cannot disagree.
	const visitItems = (
		gate: (appt: OrderRow["appointments"][number]) => BlockReason,
		onSelect: (event: Event, appt: OrderRow["appointments"][number]) => void,
	) =>
		order.appointments?.map((appt) => {
			const reason = gate(appt);
			return (
				<DisabledReasonTooltip key={appt.id} reason={reason} side="left">
					<DropdownMenuItem
						disabled={!!reason}
						onSelect={(event) => onSelect(event, appt)}
					>
						Visit {appt.visitNumber}/{appt.totalPackageVisits}
					</DropdownMenuItem>
				</DisabledReasonTooltip>
			);
		});

	// Built once and placed by ACTION_MENU_REGROUP, so both layouts share one gate and
	// one handler per entry.
	const soapReportItems = (labelled: boolean) =>
		order.appointments?.map((appt) => (
			<DropdownMenuItem
				key={appt.id}
				onSelect={() => {
					onDownloadSoapReport(
						appt.id,
						appt.visitNumber,
						appt.totalPackageVisits,
					);
				}}
			>
				{labelled
					? tappt("button.order_actions.menus.download.soap_visit", {
							visit: appt.visitNumber,
							total: appt.totalPackageVisits,
						})
					: `Visit ${appt.visitNumber}/${appt.totalPackageVisits}`}
			</DropdownMenuItem>
		));
	const finalSoapItem = (
		<DisabledReasonTooltip reason={blocked.downloadFinalSoap} side="left">
			<DropdownMenuItem
				onSelect={() => {
					onDownloadSoapFinal();
				}}
				disabled={!!blocked.downloadFinalSoap}
			>
				<DownloadIcon className="opacity-60" aria-hidden="true" />
				{tappt(
					regroup
						? "button.order_actions.menus.download.final_soap_short"
						: "button.order_actions.menus.download.final_soap",
				)}
			</DropdownMenuItem>
		</DisabledReasonTooltip>
	);
	const invoiceItem = (
		<DropdownMenuItem
			onSelect={(e) => {
				e.preventDefault();
				onDownloadInvoice();
			}}
		>
			<DownloadIcon className="opacity-60" aria-hidden="true" />
			{tappt(
				regroup
					? "button.order_actions.menus.download.invoice_short"
					: "button.order_actions.menus.download.invoice",
			)}
		</DropdownMenuItem>
	);
	// Same gate as the single reschedule — a one-visit order still opens the bulk page,
	// which is only this order's visits in a form.
	const bulkRescheduleItem = (label: string) => (
		<DisabledReasonTooltip reason={blocked.reschedule} side="left">
			<DropdownMenuItem
				onSelect={(e) => {
					e.preventDefault();
					openBulkReschedulePage(order.id);
				}}
				disabled={!!blocked.reschedule}
			>
				<Clock3Icon className="opacity-60" aria-hidden="true" />
				{label}
			</DropdownMenuItem>
		</DisabledReasonTooltip>
	);
	const bulkStatusItem = (label: string) => (
		<DisabledReasonTooltip reason={blocked.updateStatus} side="left">
			<DropdownMenuItem
				onSelect={(e) => {
					e.preventDefault();
					onOpenBulkStatus(order);
				}}
				disabled={!!blocked.updateStatus}
			>
				<ActivityIcon className="opacity-60" aria-hidden="true" />
				{label}
			</DropdownMenuItem>
		</DisabledReasonTooltip>
	);

	return (
		<DropdownMenu>
			{/* Lives outside the menu content, which unmounts when the menu closes. */}
			<DeleteDocumentDialog
				target={deleting}
				onClose={() => setDeleting(null)}
				reloadOnly={["adminPortal", "flash", "errors", "orders"]}
			/>
			<MitraAssignmentReasonDialog
				target={mitraReasonTarget}
				options={globalProps.mitraAssignmentReasons || []}
				onClose={() => setMitraReasonTarget(null)}
			/>
			<StopPackageDialog
				target={stopTarget}
				onClose={() => setStopTarget(null)}
			/>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="size-8">
					<span className="sr-only">{tappt("button.order_actions.label")}</span>
					<MoreHorizontalIcon className="w-4 h-4" />
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="end">
				<DropdownMenuGroup>
					<DropdownMenuItem
						onSelect={(e) => {
							e.preventDefault();
							onOpenDetail(order.id);
						}}
					>
						<EyeIcon className="opacity-60" aria-hidden="true" />
						{tappt("button.order_actions.menus.view_details")}
					</DropdownMenuItem>

					<DropdownMenuItem
						onSelect={(e) => {
							e.preventDefault();
							onViewOnGoogleMaps();
						}}
						disabled={!order.latitude || !order.longitude}
					>
						<MapPinIcon className="opacity-60" aria-hidden="true" />
						{tappt("button.order_actions.menus.view_on_google_maps")}
					</DropdownMenuItem>

					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<CopyIcon className="opacity-60" aria-hidden="true" />
							{tappt("button.order_actions.menus.copy.label")}
						</DropdownMenuSubTrigger>

						<DropdownMenuPortal>
							<DropdownMenuSubContent>
								<DropdownMenuItem
									onSelect={onCopyInvoiceUrl}
									disabled={!order.invoiceUrl}
								>
									{tappt("button.order_actions.menus.copy.invoice_url")}
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyRegNumber}
									disabled={!order.registrationNumber}
								>
									{tappt("button.order_actions.menus.copy.appt_reg_number")}
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyInvoiceNumber}
									disabled={!order.invoiceNumber}
								>
									{tappt("button.order_actions.menus.copy.invoice_number")}
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyPatientId}
									disabled={!order.patientId}
								>
									{tappt("button.order_actions.menus.copy.patient_id")}
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyTherapistId}
									disabled={!order.therapistId}
								>
									{tappt("button.order_actions.menus.copy.therapist_id")}
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={onCopyVisits}
									disabled={!order.appointments?.length}
								>
									{tappt("button.order_actions.menus.copy.visits_schedule")}
								</DropdownMenuItem>
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>

					<ChatCopyMenu
						endpoint={`/api/v1/appointments/orders/${order.id}/chat-copy`}
						templates={ORDER_CHAT_COPY_TEMPLATES}
						label={tappt("button.order_actions.menus.chat_copy")}
					/>

					{regroup ? (
						// One entry for every download; the per-visit SOAP reports sit flat in
						// it rather than in a third level of submenu.
						<DropdownMenuSub>
							<DropdownMenuSubTrigger>
								<DownloadIcon className="opacity-60" aria-hidden="true" />
								{tappt("button.order_actions.menus.download.label")}
							</DropdownMenuSubTrigger>
							<DropdownMenuPortal>
								<DropdownMenuSubContent>
									{soapReportItems(true)}
									{!!order.appointments?.length && <DropdownMenuSeparator />}
									{finalSoapItem}
									{invoiceItem}
								</DropdownMenuSubContent>
							</DropdownMenuPortal>
						</DropdownMenuSub>
					) : (
						<>
							<DropdownMenuSub>
								<DropdownMenuSubTrigger disabled={!order.appointments?.length}>
									<DownloadIcon className="opacity-60" aria-hidden="true" />
									{tappt("button.order_actions.menus.download.soap")}
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent>
										{soapReportItems(false)}
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
							{finalSoapItem}
							{invoiceItem}
						</>
					)}

					<DisabledReasonTooltip
						reason={blocked.sendFeedbackReminder}
						side="left"
					>
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								onOpenFeedbackDialog(order.id, order.registrationNumber);
							}}
							disabled={!!blocked.sendFeedbackReminder}
						>
							<MailIcon className="opacity-60" aria-hidden="true" />
							{tappt("button.order_actions.menus.send_feedback_reminder")}
						</DropdownMenuItem>
					</DisabledReasonTooltip>
				</DropdownMenuGroup>

				<DropdownMenuSeparator />

				<DropdownMenuGroup>
					{!regroup && (
						<>
							{bulkRescheduleItem(tappt("button.bulk_reschedule"))}
							{bulkStatusItem(tappt("button.bulk_update_status"))}
						</>
					)}

					<DropdownMenuSub>
						<DisabledReasonTooltip reason={blocked.reschedule} side="left">
							<DropdownMenuSubTrigger disabled={!!blocked.reschedule}>
								<Clock3Icon className="opacity-60" aria-hidden="true" />
								{tappt("button.reschedule")}
							</DropdownMenuSubTrigger>
						</DisabledReasonTooltip>
						<DropdownMenuPortal>
							<DropdownMenuSubContent>
								{regroup && (
									<>
										{bulkRescheduleItem(tappt("button.all_visits_bulk"))}
										<DropdownMenuSeparator />
									</>
								)}
								{visitItems(
									(appt) =>
										blockReason.reschedule(appt as unknown as Appointment),
									(e, appt) => {
										e.preventDefault();
										openReschedulePage(appt.id);
									},
								)}
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>

					<DropdownMenuSub>
						<DisabledReasonTooltip reason={blocked.updateStatus} side="left">
							<DropdownMenuSubTrigger disabled={!!blocked.updateStatus}>
								<ActivityIcon className="opacity-60" aria-hidden="true" />
								{tappt("button.update_status")}
							</DropdownMenuSubTrigger>
						</DisabledReasonTooltip>
						<DropdownMenuPortal>
							<DropdownMenuSubContent>
								{regroup && (
									<>
										{bulkStatusItem(tappt("button.all_visits_bulk"))}
										<DropdownMenuSeparator />
									</>
								)}
								{visitItems(
									(appt) =>
										blockReason.updateStatus(appt as unknown as Appointment),
									(e, appt) => {
										e.preventDefault();
										openUpdateStatusPage(appt.id);
									},
								)}
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>

					<DropdownMenuSub>
						<DisabledReasonTooltip
							reason={blocked.editMedicalRecord}
							side="left"
						>
							<DropdownMenuSubTrigger disabled={!!blocked.editMedicalRecord}>
								<Stethoscope className="opacity-60" aria-hidden="true" />
								{tappt("button.edit_medical_record")}
							</DropdownMenuSubTrigger>
						</DisabledReasonTooltip>
						<DropdownMenuPortal>
							<DropdownMenuSubContent>
								{visitItems(
									(appt) =>
										blockReason.editMedicalRecord(
											appt as unknown as Appointment,
										),
									(e, appt) => {
										e.preventDefault();
										openEditMedicalRecord(appt.id);
									},
								)}
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>

					{/* No order-status gate, unlike the row above: a finished order is exactly
					    the one whose notes still need correcting. Per visit it is the SOAP's
					    own presence that decides, since upstream answers 404 without one. */}
					<DropdownMenuSub>
						<DisabledReasonTooltip reason={blocked.editSoap} side="left">
							<DropdownMenuSubTrigger disabled={!!blocked.editSoap}>
								<NotebookPen className="opacity-60" aria-hidden="true" />
								{tappt("button.edit_soap")}
							</DropdownMenuSubTrigger>
						</DisabledReasonTooltip>
						<DropdownMenuPortal>
							<DropdownMenuSubContent>
								{visitItems(
									(appt) =>
										blockReason.editSoap(appt as unknown as Appointment),
									(e, appt) => {
										e.preventDefault();
										openEditSoap(appt.id);
									},
								)}
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>

					<DisabledReasonTooltip reason={blocked.changePackage} side="left">
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								openChangePackage();
							}}
							disabled={!!blocked.changePackage}
						>
							<PackageIcon className="opacity-60" aria-hidden="true" />
							{tappt("button.order_actions.menus.change_package")}
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					<DisabledReasonTooltip reason={blocked.changeVoucher} side="left">
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								openChangeVoucher();
							}}
							disabled={!!blocked.changeVoucher}
						>
							<TicketIcon className="opacity-60" aria-hidden="true" />
							{tappt("button.order_actions.menus.change_voucher")}
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					{globalProps.adminPortal?.featureFlags
						?.mitraAssignmentReasonEnabled && (
						<DisabledReasonTooltip
							reason={blocked.changeMitraReason}
							side="left"
						>
							<DropdownMenuItem
								disabled={!!blocked.changeMitraReason}
								onSelect={() =>
									setMitraReasonTarget({
										url: `/admin-portal/appointments/orders/${order.id}/mitra-assignment-reason`,
										visits: order.mitraVisits ?? [],
									})
								}
							>
								<UserPenIcon className="opacity-60" aria-hidden="true" />
								{tappt("button.order_actions.menus.change_mitra_reason")}
							</DropdownMenuItem>
						</DisabledReasonTooltip>
					)}

					{canReconcileBonus && (
						<ReconcileBonusMenuItem
							orderId={order.bonusRoReconcilable ? order.id : null}
						/>
					)}

					<DisabledReasonTooltip reason={blocked.updatePayment} side="left">
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								openUpdatePayment();
							}}
							disabled={!!blocked.updatePayment}
						>
							<BanknoteIcon className="opacity-60" aria-hidden="true" />
							{tappt("button.update_payment")}
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					<DisabledReasonTooltip reason={blocked.regenerateInvoice} side="left">
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								openRegenerateInvoice();
							}}
							disabled={!!blocked.regenerateInvoice}
						>
							<BanknoteIcon className="opacity-60" aria-hidden="true" />
							{tappt("button.order_actions.menus.regenerate_invoice")}
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					<DisabledReasonTooltip reason={blocked.updatePic} side="left">
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								openUpdatePicForm();
							}}
							disabled={!!blocked.updatePic}
						>
							<CctvIcon className="opacity-60" aria-hidden="true" />
							{tappt("button.update_pic")}
						</DropdownMenuItem>
					</DisabledReasonTooltip>

					<DisabledReasonTooltip reason={blocked.updateAddress} side="left">
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								if (order.firstAppointmentId) {
									router.get(
										pageURL,
										{ update_address: order.firstAppointmentId },
										{
											only: [
												"adminPortal",
												"flash",
												"errors",
												"selectedAppointment",
												"optionsData",
											],
											preserveScroll: true,
											preserveState: true,
										},
									);
								}
							}}
							disabled={!!blocked.updateAddress}
						>
							<MapPinned className="opacity-60" aria-hidden="true" />
							{tappt("button.update_address")}
						</DropdownMenuItem>
					</DisabledReasonTooltip>
				</DropdownMenuGroup>

				<DropdownMenuSeparator />

				<DropdownMenuGroup>
					{/* Same whitelist and per-visit gate as Update SOAP: each item needs the
					    document it would delete, since upstream answers 404 without one. An
					    order where no visit has it greys out the whole submenu. */}
					{(
						[
							{
								kind: "soap",
								reasonKey: "deleteSoap",
								label: tappt("button.delete_soap"),
								has: (appt) => appt.hasSoap,
							},
							{
								kind: "evidence",
								reasonKey: "deleteEvidence",
								label: tappt("button.delete_evidence"),
								has: (appt) => appt.hasEvidence,
							},
						] satisfies {
							kind: DeletableDocument;
							reasonKey: "deleteSoap" | "deleteEvidence";
							label: string;
							has: (appt: OrderRow["appointments"][number]) => boolean;
						}[]
					).map(({ kind, reasonKey, label, has }) => (
						<DropdownMenuSub key={kind}>
							<DisabledReasonTooltip reason={blocked[reasonKey]} side="left">
								<DropdownMenuSubTrigger
									disabled={!!blocked[reasonKey]}
									className="text-destructive focus:text-destructive"
								>
									<Trash2Icon className="opacity-60" aria-hidden="true" />
									{label}
								</DropdownMenuSubTrigger>
							</DisabledReasonTooltip>
							<DropdownMenuPortal>
								<DropdownMenuSubContent>
									{visitItems(
										(appt) =>
											deleteDocumentReason(kind, has(appt), canUpdateSoap),
										(_e, appt) =>
											setDeleting({
												appointmentId: String(appt.id),
												kind,
												visitNumber: appt.visitNumber,
												totalVisits: appt.totalPackageVisits,
												therapistName: appt.therapistName,
												hasOtherDocument:
													kind === "soap" ? appt.hasEvidence : appt.hasSoap,
											}),
									)}
								</DropdownMenuSubContent>
							</DropdownMenuPortal>
						</DropdownMenuSub>
					))}

					<DropdownMenuSub>
						<DisabledReasonTooltip
							reason={blocked.stopPackage && blocked.cancelBooking}
							side="left"
						>
							<DropdownMenuSubTrigger
								className="text-destructive focus:text-destructive data-[state=open]:text-destructive"
								disabled={!!(blocked.stopPackage && blocked.cancelBooking)}
							>
								<BanIcon className="opacity-60" aria-hidden="true" />
								{tappt("button.cancel_booking")}
							</DropdownMenuSubTrigger>
						</DisabledReasonTooltip>
						<DropdownMenuPortal>
							<DropdownMenuSubContent className="w-72">
								<DisabledReasonTooltip reason={blocked.stopPackage} side="left">
									<DropdownMenuItem
										className="items-start"
										disabled={!!blocked.stopPackage}
										onSelect={() =>
											setStopTarget({
												orderId: order.id,
												totalAmount: order.totalAmount,
												discountAmount: order.discountAmount,
												voucherCode: order.voucherCode,
												pricePerVisit: order.pricePerVisit,
												packageName: order.packageName,
												registrationNumber: order.registrationNumber,
												patientName: order.patientName,
												serviceName: order.serviceName,
												city: order.appointments?.[0]?.locationCity ?? null,
											})
										}
									>
										<OctagonXIcon
											className="mt-0.5 opacity-60"
											aria-hidden="true"
										/>
										<span className="grid gap-0.5">
											<span>
												{tappt("button.order_actions.menus.stop_package")}
											</span>
											<span className="text-xs text-muted-foreground">
												{tappt("button.order_actions.menus.stop_package_hint")}
											</span>
										</span>
									</DropdownMenuItem>
								</DisabledReasonTooltip>
								<DisabledReasonTooltip
									reason={blocked.cancelBooking}
									side="left"
								>
									<DropdownMenuItem
										className="items-start text-destructive focus:text-destructive"
										onSelect={(e) => {
											e.preventDefault();
											if (order.firstAppointmentId) {
												openCancelPage(order.firstAppointmentId);
											}
										}}
										disabled={!!blocked.cancelBooking}
									>
										<BanIcon className="mt-0.5 opacity-60" aria-hidden="true" />
										<span className="grid gap-0.5">
											<span>
												{tappt(
													"button.order_actions.menus.cancel_entire_order",
												)}
											</span>
											<span className="text-xs text-muted-foreground">
												{tappt(
													"button.order_actions.menus.cancel_entire_order_hint",
												)}
											</span>
										</span>
									</DropdownMenuItem>
								</DisabledReasonTooltip>
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

// The row's only marker for work the system created but cannot finish. A toast
// tells whoever pressed the button; this tells everyone else, tomorrow.
const OPEN_PAYMENT_TAG_STYLES: Record<string, string> = {
	needs_invoice: "border-amber-500/60 text-amber-700 dark:text-amber-400",
	charge: "border-blue-500/60 text-blue-700 dark:text-blue-400",
	refund: "border-violet-500/60 text-violet-700 dark:text-violet-400",
};

// fisiohome-backend's rule: an order is admin-booked exactly when a Draft points at it.
// A Draft the customer filled in and an admin then submitted is Self-Booked on the Drafts
// page but Admin-Booked here; an admin did book it, through the booking form.
const CREATOR_TAGS: Record<
	CreatorType,
	{ key: string; label: string; hint: string }
> = {
	admin: {
		key: "admin",
		label: "order_tags.admin",
		hint: "order_tags.admin_hint",
	},
	customer: {
		key: "self-booked",
		label: "drafts.badges.self_booked",
		hint: "order_tags.customer_hint",
	},
};

const CreatorBadge = ({ creatorType }: { creatorType: CreatorType }) => {
	const { t } = useTranslation("appointments");

	return (
		<Badge
			variant="outline"
			className="text-[10px] uppercase !px-2 rounded-full text-nowrap text-muted-foreground"
		>
			{t(CREATOR_TAGS[creatorType].label)}
		</Badge>
	);
};

const OrderTagsCell = ({
	order,
	onOpenSettle,
}: {
	order: OrderRow;
	onOpenSettle: (orderId: string) => void;
}) => {
	const { t } = useTranslation("appointments");
	const tag = order.openPayment;
	const creator = CREATOR_TAGS[order.creatorType];
	const info: RowTag[] = [
		{
			key: creator.key,
			label: t(creator.label),
			hint: t(creator.hint),
			variant: "outline",
		},
	];
	if (order.mitraVisit) {
		info.push({
			key: "mitra-visit",
			label: t("drafts.badges.mitra_visit"),
			hint: t("drafts.badges.mitra_visit_hint"),
			variant: "accent",
		});
	}

	if (!tag) {
		return <RowTags tags={info} />;
	}

	// Three tags, not two: a charge with no payment link needs the invoice issued
	// before anyone can pay it, which is a different job from chasing a payer.
	const key = tag.needsInvoice ? "needs_invoice" : tag.kind;

	// The payment tag is an action, so it stays outside RowTags and never folds into the count.
	return (
		<div className="flex items-center gap-1 w-max">
			<OpenPaymentTag order={order} tagKey={key} onOpenSettle={onOpenSettle} />
			<RowTags tags={info} />
		</div>
	);
};

const OpenPaymentTag = ({
	order,
	tagKey: key,
	onOpenSettle,
}: {
	order: OrderRow;
	tagKey: string;
	onOpenSettle: (orderId: string) => void;
}) => {
	const { t } = useTranslation("appointments");

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					{/* A real button, not a span: the tag is the shortest route to the
					    actions that close it, and a keyboard user needs to reach it. */}
					<button
						type="button"
						onClick={() => onOpenSettle(order.id)}
						className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						<Badge
							variant="outline"
							className={cn(
								"text-[10px] uppercase !px-2 rounded-full text-nowrap cursor-pointer bg-background",
								OPEN_PAYMENT_TAG_STYLES[key],
							)}
						>
							{t(`payments.tags.${key}.label`)}
						</Badge>
					</button>
				</TooltipTrigger>
				<TooltipContent side="top" className="max-w-64">
					<span>{t(`payments.tags.${key}.hint`)}</span>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
};

const orderColumns = ({
	pageURL,
	openOrderDetail,
	openSettlePayments,
	onOpenFeedbackDialog,
	onOpenBulkStatus,
	dateFnsLocale,
	timeFormatDateFns,
}: {
	pageURL: string;
	openOrderDetail: (orderId: string) => void;
	openSettlePayments: (orderId: string) => void;
	onOpenFeedbackDialog: (orderId: string, registrationNumber: string) => void;
	onOpenBulkStatus: (order: OrderRow) => void;
	dateFnsLocale: Locale;
	timeFormatDateFns: string;
}): ColumnDef<OrderRow>[] => [
	{
		accessorKey: "registrationNumber",
		header: "Reg. No",
		meta: {
			headerClassName: "sticky left-0 z-10 bg-card",
			cellClassName: "sticky left-0 z-10 bg-card",
		},
		cell: ({ row }) => {
			const { fullUrl } = populateQueryParams(pageURL, {
				view_order: row.original.id,
			});

			return (
				// The copy button sits outside the Link on purpose: nested inside it,
				// every copy would also navigate.
				<div className="flex items-center gap-1.5">
					<Link
						href={fullUrl}
						preserveScroll
						preserveState
						only={["adminPortal", "flash", "errors", "selectedOrder"]}
					>
						<span className="font-mono text-sm font-bold text-nowrap text-primary hover:underline">
							{row.original.registrationNumber}
						</span>
					</Link>
					<CopyButton
						value={row.original.registrationNumber}
						label={i18n.t("appointments:common.copy_value", {
							value: row.original.registrationNumber,
						})}
					/>
				</div>
			);
		},
	},
	{
		accessorKey: "patientName",
		header: "Patient",
		cell: ({ row }) => {
			const firstAppointment = row.original.appointments?.[0];
			const locationCity = firstAppointment?.locationCity;

			return (
				<div className="max-w-[200px]">
					{/* One line, like the Package column beside it — a two-word name broken
					    across two lines makes the row taller than the data deserves. Capped
					    and truncated rather than free-running: an unbounded nowrap cell lets
					    one long name push the whole table into horizontal scroll. */}
					<div className="flex flex-col text-nowrap">
						<span
							title={row.original.patientName || "—"}
							className="text-sm uppercase font-medium truncate"
						>
							{row.original.patientName || "—"}
						</span>
						{locationCity && (
							<span className="text-sm text-muted-foreground/75 truncate">
								{locationCity}
							</span>
						)}
					</div>
				</div>
			);
		},
	},
	{
		id: "documents",
		header: () => <div className="text-center">Docs</div>,
		cell: ({ row }) => <DocumentCoverageCell order={row.original} />,
	},
	{
		accessorKey: "packageName",
		header: "Package",
		cell: ({ row }) => {
			const { serviceName, packageName, numberOfVisit } = row.original;
			const visitLabel =
				numberOfVisit && numberOfVisit > 0 ? ` - ${numberOfVisit} visits` : "";

			return (
				<div className="flex flex-col text-nowrap">
					<span className="text-sm">
						{serviceName?.replace(/_/g, " ") || "—"}
					</span>
					<span className="text-sm text-muted-foreground/75">
						{packageName || "—"}
						{visitLabel}
					</span>
				</div>
			);
		},
	},
	{
		accessorKey: "paymentStatus",
		header: () => <div className="text-center">Payment</div>,
		cell: ({ row }) => (
			<div className="flex items-center justify-center">
				<Badge
					className={cn(
						"text-[10px] border p-0 px-1 rounded-full font-bold",
						PAYMENT_STATUS_STYLES[row.original.paymentStatus] ||
							"bg-background hover:bg-background/80",
					)}
				>
					{row.original.paymentStatus.replace(/_/g, " ")}
				</Badge>
			</div>
		),
	},
	{
		accessorKey: "status",
		header: () => <div className="text-center">Status</div>,
		cell: ({ row }) => (
			<div className="flex items-center justify-center">
				<Badge
					className={cn(
						"text-[10px] border p-0 px-1 rounded-full font-bold",
						ORDER_STATUS_STYLES[row.original.status] ||
							"bg-background hover:bg-background/80",
					)}
				>
					{row.original.status.replace(/_/g, " ")}
				</Badge>
			</div>
		),
	},
	{
		accessorKey: "invoiceNumber",
		header: "Invoice",
		cell: ({ row }) =>
			row.original.invoiceNumber ? (
				<span className="inline-flex items-center gap-1.5">
					<span className="select-all text-sm text-muted-foreground text-nowrap italic">
						{row.original.invoiceNumber}
					</span>
					<CopyButton
						value={row.original.invoiceNumber}
						label={i18n.t("appointments:common.copy_value", {
							value: row.original.invoiceNumber,
						})}
					/>
				</span>
			) : (
				<span className="text-sm text-muted-foreground">—</span>
			),
	},
	{
		accessorKey: "createdAt",
		header: "Created Date",
		cell: ({ row }) => {
			const value = row.original.createdAt;
			const timeToken =
				dateFnsLocale.code === "id" ? "HH:mm" : timeFormatDateFns;
			return (
				<div className="text-sm text-muted-foreground/75 text-nowrap">
					<p className="font-medium text-foreground">
						{formatDate(value, dateFnsLocale)}
					</p>
					<p>{format(new Date(value), timeToken, { locale: dateFnsLocale })}</p>
				</div>
			);
		},
	},
	{
		id: "tags",
		header: "Tags",
		cell: ({ row }) => (
			<OrderTagsCell order={row.original} onOpenSettle={openSettlePayments} />
		),
	},
	{
		id: "actions",
		header: "",
		cell: ({ row }) => (
			<div className="flex justify-end">
				<OrderActionsCell
					row={row}
					onOpenDetail={openOrderDetail}
					onOpenFeedbackDialog={onOpenFeedbackDialog}
					onOpenBulkStatus={onOpenBulkStatus}
				/>
			</div>
		),
	},
];

// ─── Order Detail Dialog ──────────────────────────────────────────────────────

function OrderDetailContent({
	order,
	onClose,
}: {
	order: OrderDetail;
	onClose?: () => void;
}) {
	const { t: tappt } = useTranslation("appointments", {
		useSuspense: false,
	});
	const { locale: dateFnsLocale, timeFormatDateFns, tzDate } = useDateContext();
	const mitraReasons =
		usePage<OrdersGlobalPageProps>().props.mitraAssignmentReasons;

	// Copies a single visit row, e.g.
	// "Visit 1: Selasa, 28 Juli 2026 (16:00 WIB) - FT Intan Rahmawati".
	const onCopyVisitLine = useCallback(
		async (appt: OrderDetail["appointments"][number]) => {
			const text = formatVisitLine(appt, {
				locale: dateFnsLocale,
				tzDate,
				timeFormat: timeFormatDateFns,
				t: tappt,
			});
			const ok = await copyToClipboard(text);
			if (ok) {
				toast.success(`Visit ${appt.visitNumber} copied to clipboard`);
			} else {
				toast.error("Failed to copy visit");
			}
		},
		[dateFnsLocale, tzDate, timeFormatDateFns, tappt],
	);

	const [selectedVisitIds, setSelectedVisitIds] = useState<Set<string>>(
		new Set(),
	);
	const [isBulkStatusDialogOpen, setIsBulkStatusDialogOpen] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: order.id isn't read in the body, but a switch to a different order must still clear the previous one's selection
	useEffect(() => {
		setSelectedVisitIds(new Set());
		setIsBulkStatusDialogOpen(false);
	}, [order.id]);

	// Status alone locks nothing — cancelled, completed and refunded visits are all
	// selectable, because reopening them is a real thing staff do. What is excluded is the
	// visit whose paperwork is done (completed, with both SOAP and evidence), the same
	// visit the single Update Status entry greys out, and every visit of a stopped package.
	// Excluded here *and* disabled on the row below, so a ticked checkbox is never silently
	// skipped on submit.
	const visitStatusReasons = useMemo(
		() =>
			new Map(
				order.appointments.map((appt) => [
					appt.id,
					firstReason(
						packageStoppedReason(order.status),
						blockReason.updateStatus(appt as unknown as Appointment),
					),
				]),
			),
		[order.appointments, order.status],
	);
	const eligibleVisitIds = useMemo(
		() =>
			order.appointments
				.filter((appt) => !visitStatusReasons.get(appt.id))
				.map((appt) => appt.id),
		[order.appointments, visitStatusReasons],
	);
	const allVisitsSelected =
		eligibleVisitIds.length > 0 &&
		eligibleVisitIds.every((id) => selectedVisitIds.has(id));
	const someVisitsSelected =
		!allVisitsSelected &&
		eligibleVisitIds.some((id) => selectedVisitIds.has(id));

	const toggleSelectAllVisits = () => {
		setSelectedVisitIds((prev) => {
			const next = new Set(prev);
			if (allVisitsSelected) {
				eligibleVisitIds.forEach((id) => next.delete(id));
			} else {
				eligibleVisitIds.forEach((id) => next.add(id));
			}
			return next;
		});
	};

	const toggleSelectVisit = (id: string) => {
		setSelectedVisitIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	return (
		<div className="space-y-4 px-1">
			{/* Overview */}
			<div className="grid grid-cols-2 md:grid-cols-3 gap-2">
				<InfoItem
					label="Reg. Number"
					value={<CopyableValue value={order.registrationNumber} />}
				/>
				<InfoItem
					label="Draft ID"
					value={<CopyableValue value={order.draftId} />}
				/>
				<InfoItem
					label="Created By"
					value={<CreatorBadge creatorType={order.creatorType} />}
				/>
				<InfoItem
					label="Patient"
					value={
						<div className="text-sm font-semibold">
							<span className="uppercase">{order.patient?.name || "—"}</span>
						</div>
					}
				/>
				<InfoItem
					label="Package"
					value={
						<div className="flex flex-col break-words">
							<span className="text-sm font-semibold">
								{order.appointments?.[0]?.serviceName?.replace(/_/g, " ") ||
									"—"}
							</span>
							<span className="text-xs italic text-muted-foreground">
								{order.package?.name || "—"}
								{order.package?.numberOfVisit
									? ` - ${order.package.numberOfVisit} visits`
									: ""}
							</span>
						</div>
					}
				/>
				<InfoItem
					label="PIC"
					// Its own row: an Order carries as many PICs as it has stages plus
					// supporting, which is more names than a third of a row can hold.
					className="col-span-2 md:col-span-3"
					value={
						order.pics?.length ? (
							// Ordered by stage, so the box reads L1 → L2 → L3 → Other rather
							// than in whatever order the pivot happens to hold.
							<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
								{[...order.pics]
									.sort((a, b) => picRoleRank(a.roles) - picRoleRank(b.roles))
									.map((pic, index) => (
										// The bullet travels inside the item it precedes, so a wrapped
										// line never ends on a dangling separator — it starts with one.
										<span key={pic.id} className="flex items-center gap-x-2">
											{index > 0 && (
												<span
													aria-hidden
													className="text-muted-foreground/60 select-none"
												>
													&bull;
												</span>
											)}
											<span className="text-sm font-semibold uppercase">
												{pic.name}
											</span>
											<PicRoleBadge roles={pic.roles} className="shrink-0" />
										</span>
									))}
							</div>
						) : (
							<span className="text-sm font-semibold">—</span>
						)
					}
				/>
			</div>

			<Separator />

			{/* Money — the bill and the payments that answer it, one section. */}
			<PaymentsPanel order={order} />

			<Separator />

			{/* Status & Timeline */}
			<div>
				<h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
					Status & Timeline
				</h4>
				<div className="grid grid-cols-3 gap-2">
					<InfoItem
						label="Order Status"
						value={
							<Badge
								className={cn(
									"text-[10px] border p-0 px-1 rounded-full font-bold",
									ORDER_STATUS_STYLES[order.status] ||
										"bg-background hover:bg-background/80",
								)}
							>
								{order.status.replace(/_/g, " ")}
							</Badge>
						}
					/>
					<InfoItem
						label="Created"
						value={formatDateTime(
							order.createdAt,
							dateFnsLocale,
							timeFormatDateFns,
						)}
					/>
					<InfoItem
						label="Last updated"
						value={formatDateTime(
							order.updatedAt,
							dateFnsLocale,
							timeFormatDateFns,
						)}
					/>
					{order.completedAt && (
						<InfoItem
							label="Completed"
							value={formatDateTime(
								order.completedAt,
								dateFnsLocale,
								timeFormatDateFns,
							)}
						/>
					)}
					{order.cancelledAt && (
						<InfoItem
							label="Cancelled"
							valueClassName=""
							value={
								<>
									<p className="font-semibold text-sm text-pretty leading-snug">
										{formatDateTime(
											order.cancelledAt,
											dateFnsLocale,
											timeFormatDateFns,
										)}
									</p>

									{order.cancelledBy && (
										<Badge
											variant="outline"
											className="text-[10px] italic border p-0 px-1 rounded-full font-bold bg-card"
										>
											By {order.cancelledBy}
										</Badge>
									)}
								</>
							}
						/>
					)}
					{order.stoppedAt && (
						<>
							<InfoItem
								label="Stopped"
								valueClassName=""
								value={
									<>
										<p className="font-semibold text-sm text-pretty leading-snug">
											{formatDateTime(
												order.stoppedAt,
												dateFnsLocale,
												timeFormatDateFns,
												tzDate,
											)}
										</p>
										<Badge
											variant="outline"
											className="text-[10px] italic border p-0 px-1 rounded-full font-bold bg-card"
										>
											By {order.stoppedByName || "—"}
										</Badge>
									</>
								}
							/>
							<InfoItem
								label="Stop Reason"
								value={
									order.stopReason
										? tappt(`stop_package_dialog.reasons.${order.stopReason}`, {
												defaultValue: order.stopReason,
											})
										: "—"
								}
							/>
							<InfoItem
								label="Original Total"
								value={formatCurrency(order.originalTotalAmount)}
							/>
						</>
					)}
				</div>
			</div>

			{order.stopNotes && (
				<InfoItem label="Stop Notes" value={order.stopNotes} />
			)}

			{order.specialNotes && (
				<InfoItem label="Special Notes" value={order.specialNotes} />
			)}

			{order.cancellationReason && (
				<InfoItem
					label="Cancellation Reason"
					value={order.cancellationReason}
					valueClassName="text-red-600"
				/>
			)}

			{order.appointments.length > 0 && (
				<>
					<Separator />
					<OrderAddressSection
						appointments={order.appointments}
						changes={order.addressChanges}
						formatChangedAt={(value) =>
							formatDateTime(value, dateFnsLocale, timeFormatDateFns, tzDate)
						}
					/>
				</>
			)}

			{/* Related Appointments */}
			{order.appointments.length > 0 && (
				<>
					<Separator />
					<div>
						<div className="flex items-center justify-between mb-2">
							<h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
								Appointments ({order.appointments.length})
							</h4>
							<div className="flex items-center gap-2">
								<Button
									variant="ghost"
									size="sm"
									className="h-7 text-xs gap-1.5"
									onClick={() =>
										window.open(
											`/admin-portal/appointments?registration_number=${order.registrationNumber}`,
											"_blank",
										)
									}
								>
									See Details
									<ExternalLinkIcon className="size-3" />
								</Button>
							</div>
						</div>
						<div className="border rounded-lg overflow-hidden">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="text-xs py-2 w-9">
											<Checkbox
												checked={allVisitsSelected}
												data-state={
													someVisitsSelected
														? "indeterminate"
														: allVisitsSelected
															? "checked"
															: "unchecked"
												}
												onCheckedChange={toggleSelectAllVisits}
												disabled={eligibleVisitIds.length === 0}
												aria-label="Select all visits"
											/>
										</TableHead>
										<TableHead className="text-xs py-2">Visit</TableHead>
										<TableHead className="text-xs py-2">Date</TableHead>
										<TableHead className="text-xs py-2">Therapist</TableHead>
										<TableHead className="text-xs py-2">Status</TableHead>
										<TableHead className="text-xs py-2 w-9">
											<span className="sr-only">Actions</span>
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{order.appointments.map((appt) => {
										const statusReason = visitStatusReasons.get(appt.id);

										return (
											<TableRow key={appt.id}>
												<TableCell className="py-1.5">
													<Checkbox
														checked={selectedVisitIds.has(appt.id)}
														onCheckedChange={() => toggleSelectVisit(appt.id)}
														disabled={!!statusReason}
														title={
															statusReason ? tappt(statusReason) : undefined
														}
														aria-label={`Select visit ${appt.visitNumber}`}
													/>
												</TableCell>
												<TableCell className="py-1.5">
													<span className="font-medium text-sm">
														#{appt.visitNumber}
													</span>
												</TableCell>
												<TableCell className="py-1.5">
													{(() => {
														if (appt.appointmentDateTime) {
															return (
																<span className="text-sm text-nowrap font-medium">
																	{formatDateTime(
																		appt.appointmentDateTime,
																		dateFnsLocale,
																		timeFormatDateFns,
																		tzDate,
																	)}
																</span>
															);
														}
														if (
															!appt.preferredDate &&
															!isTimePreference(appt.timePreference)
														) {
															return (
																<span className="text-sm text-muted-foreground">
																	—
																</span>
															);
														}
														const stageLabel =
															appt.status === "pending_mitra_approval"
																? tappt("schedule_preference.negotiating")
																: appt.status === "pending_therapist_assignment"
																	? tappt(
																			"schedule_preference.awaiting_therapist",
																		)
																	: tappt("schedule_preference.tentative");
														return (
															<div className="text-sm text-nowrap space-y-0.5">
																<p className="font-medium">
																	{appt.preferredDate
																		? formatDate(
																				appt.preferredDate,
																				dateFnsLocale,
																				tzDate,
																			)
																		: "—"}
																</p>
																<p className="text-[10px] uppercase tracking-wider text-muted-foreground/75">
																	{stageLabel}
																</p>
																{isTimePreference(appt.timePreference) ? (
																	<p className="italic text-muted-foreground">
																		{timePreferenceShortWindow(
																			appt.timePreference,
																			tappt,
																			{
																				locale: dateFnsLocale,
																				timeFormat: timeFormatDateFns,
																			},
																		)}
																	</p>
																) : null}
															</div>
														);
													})()}
												</TableCell>
												<TableCell className="py-1.5">
													<span className="text-sm">
														{appt.therapistName || "—"}
													</span>
													{appt.mitraAssignmentReason && (
														<p className="text-xs text-muted-foreground text-pretty">
															{findReasonLabel(
																mitraReasons,
																appt.mitraAssignmentReason,
																i18n.language,
															)}
														</p>
													)}
												</TableCell>
												<TableCell className="py-1.5">
													<Badge
														className={cn(
															"text-[10px] border p-0 px-1 rounded-full font-bold",
															appointmentBadgeClass(appt.status),
														)}
													>
														{tappt(`statuses.${appt.status}`)}
													</Badge>
												</TableCell>
												<TableCell className="py-1.5">
													<Button
														variant="ghost"
														size="icon"
														className="size-7"
														title={tappt(
															"button.order_actions.menus.copy.visit_schedule",
														)}
														onClick={() => onCopyVisitLine(appt)}
													>
														<CopyIcon className="size-3.5" />
														<span className="sr-only">
															{tappt(
																"button.order_actions.menus.copy.visit_schedule",
															)}
														</span>
													</Button>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</div>

						{selectedVisitIds.size > 0 && (
							<div className="flex items-center justify-between gap-2 mt-2 px-3 py-2 rounded-md bg-primary/10 border border-primary/20">
								<span className="text-xs font-medium text-primary">
									{selectedVisitIds.size}{" "}
									{selectedVisitIds.size === 1 ? "visit" : "visits"} selected
								</span>
								<div className="flex items-center gap-2">
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="h-7 text-xs"
										onClick={() => setSelectedVisitIds(new Set())}
									>
										{tappt("button.clear_selection")}
									</Button>
									<Button
										type="button"
										size="sm"
										className="h-7 text-xs"
										onClick={() => setIsBulkStatusDialogOpen(true)}
									>
										{tappt("button.bulk_update_status")}
									</Button>
								</div>
							</div>
						)}
					</div>
				</>
			)}

			<ResponsiveDialog
				isOpen={isBulkStatusDialogOpen}
				title={tappt("button.bulk_update_status")}
				description={tappt("modal.bulk_update_status.description_selected")}
				onOpenChange={setIsBulkStatusDialogOpen}
			>
				<BulkUpdateStatusForm
					orderId={order.id}
					appointmentIds={Array.from(selectedVisitIds)}
					onSuccess={() => {
						setSelectedVisitIds(new Set());
						setIsBulkStatusDialogOpen(false);
					}}
				/>
			</ResponsiveDialog>

			{/* Close Button */}
			<div className="hidden md:flex justify-end pt-2">
				<Button variant="outline" size="sm" onClick={onClose}>
					Close
				</Button>
			</div>
		</div>
	);
}

function InfoItem({
	label,
	value,
	valueClassName,
	className,
}: {
	label: string;
	value: React.ReactNode;
	valueClassName?: string;
	// For the box itself — a column span, where one item needs the whole row.
	className?: string;
}) {
	return (
		<div
			className={cn(
				"min-w-0 space-y-0.5 rounded-md bg-muted px-3 py-2",
				className,
			)}
		>
			<p className="text-[11px] uppercase text-muted-foreground tracking-wide">
				{label}
			</p>
			{typeof value === "string" ? (
				<p
					className={cn(
						"font-semibold text-sm text-pretty leading-snug",
						valueClassName,
					)}
				>
					{value}
				</p>
			) : (
				<div className={valueClassName}>{value}</div>
			)}
		</div>
	);
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const PAYMENT_STATUS_OPTIONS = [
	{ value: "all", label: "All Payment Status" },
	{ value: "UNPAID", label: "Unpaid" },
	{ value: "PARTIALLY_PAID", label: "Partially Paid" },
	{ value: "PAID", label: "Paid" },
	{ value: "OVERPAID", label: "Overpaid" },
	{ value: "REFUNDED", label: "Refunded" },
];

// Order#filter_by_tag. The creator tag is filtered by Created By, not here.
const TAG_OPTIONS = [
	{ value: "all", label: "All Tags" },
	{ value: "mitra-visit", label: "Mitra Visit" },
	{ value: "open-payment", label: "Open Payment" },
];

const CREATED_BY_OPTIONS = [
	{ value: "all", label: "All Creators" },
	{ value: "admin", label: "Admin-Booked" },
	{ value: "customer", label: "Self-Booked" },
];

// An order holds several visits, so this is not a yes/no question: "partial" is the state
// worth chasing — someone filed the paperwork for visit 1 and stopped. "any" is this page's
// usual no-filter sentinel and is the only value that never reaches the server; the other
// three are the coverages Order#filter_by_document_coverage understands.
const DOCUMENT_COVERAGE_OPTIONS = (document: string) => [
	{ value: "any", label: `${document}: Any` },
	{ value: "all", label: `${document}: All Visits` },
	{ value: "partial", label: `${document}: Some Visits` },
	{ value: "none", label: `${document}: No Visits` },
];

export default function AppointmentOrders() {
	const { props: globalProps, url: pageURL } = usePage<OrdersGlobalPageProps>();
	const isMobile = useIsMobile();

	// ── Filters ────────────────────────────────────────────────────────────
	const [filters, setFilters] = useState(() => {
		const q = globalProps?.adminPortal?.currentQuery;
		return {
			search: q?.search || "",
			paymentStatus: q?.paymentStatus || "all",
			status: q?.status || "all",
			createdBy: q?.created_by || "all",
			tag: q?.tag || "all",
			hasSoap: q?.hasSoap || "any",
			hasEvidence: q?.hasEvidence || "any",
		};
	});

	const [isSearching, setIsSearching] = useState(false);
	const [preferences, savePreferences] = useLocalStorage(
		PREFERENCES_STORAGE_KEY,
		{
			isShowFilter: false,
		},
	);

	const handleToggleFilter = useCallback(() => {
		savePreferences((prev) => ({ ...prev, isShowFilter: !prev.isShowFilter }));
	}, [savePreferences]);

	const handleResetAllFilters = useCallback(() => {
		setFilters({
			search: "",
			paymentStatus: "all",
			status: "all",
			createdBy: "all",
			tag: "all",
			hasSoap: "any",
			hasEvidence: "any",
		});
		const { fullUrl } = populateQueryParams(
			pageURL,
			deepTransformKeysToSnakeCase({
				search: null,
				paymentStatus: null,
				status: null,
				createdBy: null,
				tag: null,
				hasSoap: null,
				hasEvidence: null,
				page: null,
			}),
		);
		startTransition(() => {
			router.get(
				fullUrl,
				{},
				{
					preserveScroll: true,
					preserveState: true,
					only: ["adminPortal", "flash", "errors", "orders"],
				},
			);
		});
	}, [pageURL]);

	const debouncedUpdate = useCallback(
		debounce((url: string) => {
			setIsSearching(true);
			startTransition(() => {
				router.get(
					url,
					{},
					{
						preserveScroll: true,
						preserveState: true,
						only: ["adminPortal", "flash", "errors", "orders"],
						onFinish: () => setIsSearching(false),
					},
				);
			});
		}, 300),
		[],
	);

	const handleFilterChange = useCallback(
		(field: keyof typeof filters, value: string) => {
			const next = { ...filters, [field]: value };
			setFilters(next);

			const { fullUrl } = populateQueryParams(
				pageURL,
				deepTransformKeysToSnakeCase({
					...next,
					paymentStatus:
						next.paymentStatus === "all" ? null : next.paymentStatus,
					status: next.status === "all" ? null : next.status,
					createdBy: next.createdBy === "all" ? null : next.createdBy,
					tag: next.tag === "all" ? null : next.tag,
					hasSoap: next.hasSoap === "any" ? null : next.hasSoap,
					hasEvidence: next.hasEvidence === "any" ? null : next.hasEvidence,
					page: null,
				}),
			);
			debouncedUpdate(fullUrl);
		},
		[filters, pageURL, debouncedUpdate],
	);

	// ── Pagination ─────────────────────────────────────────────────────────
	const metadata = useMemo(
		() => globalProps?.orders?.metadata ?? null,
		[globalProps?.orders?.metadata],
	);

	const changePage = useCallback(
		(
			type: "prev" | "next" | "first" | "last" | "limit" | "page",
			value?: string,
		) => {
			if (!metadata) return;

			let url: string | undefined;
			switch (type) {
				case "prev":
					url = metadata.prevUrl;
					break;
				case "next":
					url = metadata.nextUrl;
					break;
				case "first":
					url = metadata.firstUrl;
					break;
				case "last":
					url = metadata.lastUrl;
					break;
				case "limit": {
					if (!value) return;
					const { fullUrl } = populateQueryParams(metadata.pageUrl, {
						limit: value,
					});
					url = fullUrl;
					break;
				}
				case "page": {
					if (!value) return;
					const { fullUrl } = populateQueryParams(metadata.pageUrl, {
						page: value,
					});
					url = fullUrl;
					break;
				}
			}

			if (!url) return;

			startTransition(() => {
				router.get(
					url,
					{},
					{
						preserveScroll: false,
						preserveState: true,
						only: ["adminPortal", "flash", "errors", "orders"],
					},
				);
			});
		},
		[metadata],
	);

	// ── Dialogs ────────────────────────────────────────────────────────────
	const canUpdateSoap = useCanUpdateSoap();
	const dialogMode = useMemo(() => {
		const q = globalProps.adminPortal?.currentQuery;
		return {
			viewOrder: !!q?.viewOrder,
			cancel: !!q?.cancel,
			updatePIC: !!q?.updatePic,
			updateStatus: !!q?.updateStatus,
			updatePayment: !!q?.updatePayment,
			changePackage: !!q?.changePackage,
			changeVoucher: !!q?.changeVoucher,
			settlePayments: !!q?.settlePayments,
			regenerateInvoice: !!q?.regenerateInvoice,
			updateAddress: !!q?.updateAddress,
			editMedicalRecord: !!q?.editMedicalRecord,
			// A hand-typed ?edit_soap must not open a form the API would refuse.
			editSoap: !!q?.editSoap && canUpdateSoap,
		};
	}, [globalProps.adminPortal?.currentQuery, canUpdateSoap]);

	const { t } = useTranslation("appointments");
	const { locale: dateFnsLocale, timeFormatDateFns } = useDateContext();

	// The deferred `orders` prop survives the partial reload the stepper issues,
	// so the table's row order stays put while only the panel swaps.
	const orderRowIds = useMemo(
		() => (globalProps?.orders?.data ?? []).map((row) => String(row.id)),
		[globalProps?.orders],
	);

	const dialogViewOrder = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.viewOrder,
			title: "Order Details",
			description: "View order details and related appointments",
			dialogWidth: "680px",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						view_order: null,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedOrder"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.viewOrder, pageURL],
	);

	const dialogCancel = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.cancel,
			title: t("modal.cancel.title"),
			description: t("modal.cancel.description"),
			dialogWidth: "650px",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						cancel: null,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedAppointment"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.cancel, pageURL, t],
	);

	const dialogUpdatePIC = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.updatePIC,
			title: t("modal.update_pic.title"),
			description: t("modal.update_pic.description"),
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						update_pic: null,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: [
									"adminPortal",
									"flash",
									"errors",
									"selectedAppointment",
									"optionsData",
								],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.updatePIC, pageURL, t],
	);

	const dialogUpdateStatus = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.updateStatus,
			title: t("modal.update_status.title"),
			description: t("modal.update_status.description"),
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						update_status: null,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: [
									"adminPortal",
									"flash",
									"errors",
									"selectedAppointment",
									"optionsData",
								],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.updateStatus, pageURL, t],
	);

	const dialogUpdatePayment = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.updatePayment,
			title: t("modal.update_payment.title"),
			description: t("modal.update_payment.description"),
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						update_payment: undefined,
						selected_order: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedOrder"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.updatePayment, pageURL, t],
	);

	const dialogSettlePayments = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.settlePayments,
			title: t("dialog.settle_payments.title"),
			description: t("dialog.settle_payments.description"),
			dialogWidth: "480px",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						settle_payments: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedOrder"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.settlePayments, pageURL, t],
	);

	const dialogChangePackage = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.changePackage,
			title: t("dialog.change_package.title"),
			description: t("dialog.change_package.description"),
			// The form carries preset rows with a line of consequence each, a price
			// delta and a conflict list; at the 380px default those all wrap to five
			// lines. Wide enough for the two-column layout the form switches to at md.
			dialogWidth: "900px",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						change_package: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: [
									"adminPortal",
									"flash",
									"errors",
									"selectedOrder",
									"optionsData",
								],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.changePackage, pageURL, t],
	);

	const dialogChangeVoucher = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.changeVoucher,
			title: t("dialog.change_voucher.title"),
			description: t("dialog.change_voucher.description"),
			// The body splits into two columns from md up — the decision on the left,
			// what it costs on the right. At 560px the two would be 260px strips and
			// every preset row would wrap to four lines. Same width as change-package.
			dialogWidth: "900px",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						change_voucher: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: [
									"adminPortal",
									"flash",
									"errors",
									"selectedOrder",
									"optionsData",
								],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.changeVoucher, pageURL, t],
	);

	const dialogRegenerateInvoice = useMemo<
		Omit<ResponsiveDialogProps, "children">
	>(
		() => ({
			isOpen: dialogMode.regenerateInvoice,
			title: t("dialog.regenerate_invoice.title"),
			description: t("dialog.regenerate_invoice.description"),
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						regenerate_invoice: undefined,
						selected_order: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedOrder"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.regenerateInvoice, pageURL, t],
	);

	const dialogUpdateAddress = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.updateAddress,
			title: t("modal.update_address.title"),
			description: t("modal.update_address.description"),
			dialogWidth: "min(1040px, calc(100vw - 2rem))",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						update_address: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedAppointment"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.updateAddress, pageURL, t],
	);

	const dialogEditMedicalRecord = useMemo<
		Omit<ResponsiveDialogProps, "children">
	>(
		() => ({
			isOpen: dialogMode.editMedicalRecord,
			title: t("modal.edit_medical_record.title"),
			description: t("modal.edit_medical_record.description"),
			dialogWidth: "650px",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						edit_medical_record: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedAppointment"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.editMedicalRecord, pageURL, t],
	);

	const dialogEditSoap = useMemo<Omit<ResponsiveDialogProps, "children">>(
		() => ({
			isOpen: dialogMode.editSoap,
			title: t("modal.edit_soap.title"),
			description: t("modal.edit_soap.description"),
			// Thirteen fields, most of them prose: narrower than this and every textarea
			// turns into a two-word column.
			dialogWidth: "min(900px, calc(100vw - 2rem))",
			onOpenChange: (value: boolean) => {
				if (!value) {
					const { fullUrl } = populateQueryParams(pageURL, {
						edit_soap: undefined,
					});
					startTransition(() => {
						router.get(
							fullUrl,
							{},
							{
								only: ["adminPortal", "flash", "errors", "selectedAppointment"],
								preserveScroll: true,
								preserveState: true,
								replace: false,
							},
						);
					});
				}
			},
		}),
		[dialogMode.editSoap, pageURL, t],
	);

	const openOrderDetail = useCallback(
		(orderId: string) => {
			// The sibling dialogs are cleared, not just left alone: all three read their
			// open state off the query string, so opening one on top of another would
			// stack them.
			const { fullUrl } = populateQueryParams(pageURL, {
				view_order: orderId,
				change_package: undefined,
				settle_payments: undefined,
			});
			startTransition(() => {
				router.get(
					fullUrl,
					{},
					{
						only: ["adminPortal", "flash", "errors", "selectedOrder"],
						preserveScroll: true,
						preserveState: true,
					},
				);
			});
		},
		[pageURL],
	);

	// Row-menu shortcut: unlike the checkbox picker in the order-detail table
	// (a subset), this applies to every eligible visit in the order at once —
	// the same "act on all visits" shape as Bulk Reschedule.
	const [bulkStatusTarget, setBulkStatusTarget] = useState<{
		orderId: string;
		appointmentIds: string[];
	} | null>(null);

	const openBulkStatusForOrder = useCallback((order: OrderRow) => {
		// "Eligible" is load-bearing: the single Update Status entry greys out a completed
		// visit holding both documents, and submitting the same visit through the bulk
		// route would walk straight around that lock.
		const appointmentIds = order.appointments
			.filter(
				(appt) => !blockReason.updateStatus(appt as unknown as Appointment),
			)
			.map((appt) => appt.id);

		if (!appointmentIds.length) {
			toast.error("No eligible visits to update");
			return;
		}

		setBulkStatusTarget({ orderId: order.id, appointmentIds });
	}, []);

	// Straight to the money that is still owed. The whole order-details panel was
	// the wrong destination for it: the admin has just created one payment row and
	// wants the three buttons that close it, not the order's full history.
	const openSettlePayments = useCallback(
		(orderId: string) => {
			// Every dialog that can hand over to this one clears itself here rather than
			// in its own onSuccess: two open dialogs stack, and the handover is the only
			// place that knows it is replacing one with another.
			const { fullUrl } = populateQueryParams(pageURL, {
				settle_payments: orderId,
				change_package: undefined,
				change_voucher: undefined,
				view_order: undefined,
			});
			startTransition(() => {
				router.get(
					fullUrl,
					{},
					{
						only: ["adminPortal", "flash", "errors", "selectedOrder"],
						preserveScroll: true,
						preserveState: true,
					},
				);
			});
		},
		[pageURL],
	);

	// ── Feedback Dialog ────────────────────────────────────────────────────
	const [feedbackDialog, setFeedbackDialog] = useState<{
		isOpen: boolean;
		orderId: string;
		registrationNumber: string;
	}>({ isOpen: false, orderId: "", registrationNumber: "" });

	const handleOpenFeedbackDialog = useCallback(
		(orderId: string, registrationNumber: string) => {
			setFeedbackDialog({ isOpen: true, orderId, registrationNumber });
		},
		[],
	);

	const handleCloseFeedbackDialog = useCallback(() => {
		setFeedbackDialog({ isOpen: false, orderId: "", registrationNumber: "" });
	}, []);

	const orders = useMemo(
		() => globalProps?.orders?.data ?? [],
		[globalProps?.orders?.data],
	);

	const tableColumns = useMemo(
		() =>
			orderColumns({
				pageURL,
				openOrderDetail,
				openSettlePayments,
				onOpenFeedbackDialog: handleOpenFeedbackDialog,
				onOpenBulkStatus: openBulkStatusForOrder,
				dateFnsLocale,
				timeFormatDateFns,
			}),
		[
			pageURL,
			openOrderDetail,
			openSettlePayments,
			handleOpenFeedbackDialog,
			openBulkStatusForOrder,
			dateFnsLocale,
			timeFormatDateFns,
		],
	);

	return (
		<>
			<Head title="Orders" />

			<PageContainer className="min-h-[100vh] flex flex-col md:grid md:min-h-min space-y-4">
				<div className="flex flex-col justify-between gap-4 md:flex-row">
					<div>
						<h1 className="text-lg font-bold tracking-tight uppercase">
							Appointment Orders
						</h1>
						<p className="w-full text-sm text-muted-foreground text-pretty md:max-w-[60ch]">
							View and manage orders associated with appointment bookings,
							including payment and invoice details.
						</p>
					</div>
				</div>

				<Separator className="bg-border" />

				<ApptViewChanger activeView="orders" />

				<div className="z-10 flex items-center justify-between gap-2">
					<div>
						<Button
							type="button"
							variant={preferences.isShowFilter ? "default" : "outline"}
							size={isMobile ? "icon" : "default"}
							className="shadow-none"
							onClick={(event) => {
								event.preventDefault();
								handleToggleFilter();
							}}
						>
							<ListFilterIcon />
							{isMobile
								? null
								: preferences.isShowFilter
									? t("button.close_filter")
									: t("button.filter")}
						</Button>
					</div>

					<div className="flex items-center gap-2">
						<PasteWaFormButton />

						<Button
							asChild
							size={isMobile ? "icon" : "default"}
							className="shadow-none"
						>
							<Link
								href={
									globalProps.adminPortal.router.adminPortal.appointment.new
								}
							>
								<PlusIcon />
								{isMobile ? null : t("button.create")}
							</Link>
						</Button>
					</div>
				</div>

				{/* Filters */}
				<AnimatePresence initial={false}>
					{preferences.isShowFilter && (
						<motion.section
							initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
							transition={{ duration: 0.3, ease: "easeOut" }}
							className="p-3 border shadow-inner rounded-xl bg-background"
						>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-2 lg:grid-cols-4 xl:grid-cols-6">
								<div className="grid gap-2 col-span-full md:col-span-2">
									<Input
										type="text"
										className="shadow-inner bg-input"
										placeholder="Search order..."
										value={filters.search}
										StartIcon={{ icon: SearchIcon }}
										isLoading={isSearching}
										EndIcon={
											filters.search
												? {
														icon: XIcon,
														isButton: true,
														handleOnClick: () => {
															handleFilterChange("search", "");
														},
													}
												: undefined
										}
										onChange={(e) =>
											handleFilterChange("search", e.target.value)
										}
									/>

									<p className="text-[0.8rem] text-pretty text-muted-foreground">
										Search by Reg. No, Invoice, Patient, or Voucher.
									</p>
								</div>

								<div className="col-span-full md:col-span-1">
									<Select
										value={filters.paymentStatus}
										onValueChange={(v) =>
											handleFilterChange("paymentStatus", v)
										}
									>
										<SelectTrigger className="shadow-inner bg-input">
											<SelectValue placeholder="Payment Status" />
										</SelectTrigger>
										<SelectContent>
											{PAYMENT_STATUS_OPTIONS.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="col-span-full md:col-span-1">
									<Select
										value={filters.status}
										onValueChange={(v) => handleFilterChange("status", v)}
									>
										<SelectTrigger className="shadow-inner bg-input">
											<SelectValue placeholder="Order Status" />
										</SelectTrigger>
										<SelectContent>
											{ORDER_STATUS_OPTIONS.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="col-span-full md:col-span-1">
									<Select
										value={filters.createdBy}
										onValueChange={(v) => handleFilterChange("createdBy", v)}
									>
										<SelectTrigger className="shadow-inner bg-input">
											<SelectValue placeholder="Created By" />
										</SelectTrigger>
										<SelectContent>
											{CREATED_BY_OPTIONS.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="col-span-full md:col-span-1">
									<Select
										value={filters.tag}
										onValueChange={(v) => handleFilterChange("tag", v)}
									>
										<SelectTrigger className="shadow-inner bg-input">
											<SelectValue placeholder="Tags" />
										</SelectTrigger>
										<SelectContent>
											{TAG_OPTIONS.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="col-span-full md:col-span-1">
									<Select
										value={filters.hasSoap}
										onValueChange={(v) => handleFilterChange("hasSoap", v)}
									>
										<SelectTrigger className="shadow-inner bg-input">
											<SelectValue placeholder="SOAP" />
										</SelectTrigger>
										<SelectContent>
											{DOCUMENT_COVERAGE_OPTIONS("SOAP").map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="col-span-full md:col-span-1">
									<Select
										value={filters.hasEvidence}
										onValueChange={(v) => handleFilterChange("hasEvidence", v)}
									>
										<SelectTrigger className="shadow-inner bg-input">
											<SelectValue placeholder="Evidence" />
										</SelectTrigger>
										<SelectContent>
											{DOCUMENT_COVERAGE_OPTIONS("Evidence").map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							<div className="flex flex-col justify-end gap-4 mt-4 md:gap-2 col-span-full md:flex-row">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="w-full md:w-fit"
									onClick={(event) => {
										event.preventDefault();
										handleToggleFilter();
									}}
								>
									{t("button.close_filter")}
								</Button>

								<Button
									type="button"
									size="sm"
									variant="ghost-destructive"
									className="w-full md:w-fit"
									onClick={(event) => {
										event.preventDefault();
										handleResetAllFilters();
									}}
								>
									{t("button.clear_all_filters")}
								</Button>
							</div>
						</motion.section>
					)}
				</AnimatePresence>

				{/* Orders Table */}
				<Deferred
					data={["orders"]}
					fallback={
						<div className="space-y-4">
							<Skeleton className="w-full h-12 rounded-lg" />
							<Skeleton className="w-full h-64 rounded-lg" />
						</div>
					}
				>
					<div className="overflow-x-auto">
						<DataTable
							columns={tableColumns}
							data={orders}
							customPagination={() =>
								metadata ? (
									<ApptPagination
										metadata={metadata}
										actions={{
											goToPrevpage: () => changePage("prev"),
											goToNextPage: () => changePage("next"),
											onChangeLimit: (value) => changePage("limit", value),
											goToPage: (value) => changePage("page", value),
											goToFirstPage: () => changePage("first"),
											goToLastPage: () => changePage("last"),
										}}
									/>
								) : null
							}
						/>
					</div>
				</Deferred>
			</PageContainer>

			{/* Order Detail Dialog */}
			{globalProps?.selectedOrder && dialogViewOrder.isOpen && (
				<ResponsiveDialog {...dialogViewOrder}>
					<div className="flex flex-col gap-4">
						<RecordStepper
							ids={orderRowIds}
							currentId={String(globalProps.selectedOrder.id)}
							param="view_order"
							only={["selectedOrder"]}
							className="px-1"
						/>

						<OrderDetailContent
							order={globalProps.selectedOrder}
							onClose={() => dialogViewOrder.onOpenChange?.(false)}
						/>
					</div>
				</ResponsiveDialog>
			)}

			{/* Update PIC Dialog */}
			{globalProps?.selectedAppointment && dialogUpdatePIC.isOpen && (
				<ResponsiveDialog {...dialogUpdatePIC}>
					<UpdatePICForm
						selectedAppointment={globalProps.selectedAppointment}
					/>
				</ResponsiveDialog>
			)}

			{/* Cancel Dialog */}
			{globalProps?.selectedAppointment && dialogCancel.isOpen && (
				<ResponsiveDialog {...dialogCancel}>
					<CancelAppointmentForm
						selectedAppointment={globalProps.selectedAppointment}
					/>
				</ResponsiveDialog>
			)}

			{/* Update Status Dialog */}
			{globalProps?.selectedAppointment && dialogUpdateStatus.isOpen && (
				<ResponsiveDialog {...dialogUpdateStatus}>
					<UpdateStatusForm
						selectedAppointment={globalProps.selectedAppointment}
					/>
				</ResponsiveDialog>
			)}

			{/* Bulk Update Status Dialog (row-menu shortcut: all eligible visits) */}
			<ResponsiveDialog
				isOpen={!!bulkStatusTarget}
				title={t("button.bulk_update_status")}
				description={t("modal.bulk_update_status.description_all")}
				onOpenChange={(open) => {
					if (!open) setBulkStatusTarget(null);
				}}
			>
				{bulkStatusTarget && (
					<BulkUpdateStatusForm
						orderId={bulkStatusTarget.orderId}
						appointmentIds={bulkStatusTarget.appointmentIds}
						onSuccess={() => setBulkStatusTarget(null)}
					/>
				)}
			</ResponsiveDialog>

			{/* Update Payment Dialog */}
			{globalProps?.selectedOrder && dialogUpdatePayment.isOpen && (
				<ResponsiveDialog {...dialogUpdatePayment}>
					<UpdatePaymentForm
						order={globalProps.selectedOrder}
						paymentStatusOptions={
							globalProps.optionsData?.paymentStatuses || []
						}
						onSuccess={() => {
							// Refresh the page data after successful update
							router.reload({ only: ["orders"] });
						}}
					/>
				</ResponsiveDialog>
			)}

			{/* Change Package Dialog */}
			{globalProps?.selectedOrder && dialogChangePackage.isOpen && (
				<ResponsiveDialog {...dialogChangePackage}>
					<ChangePackageForm
						order={globalProps.selectedOrder}
						packages={globalProps.optionsData?.servicePackages || []}
						onSuccess={(leavesPaymentOpen) => {
							// Refresh the page data after successful change
							router.reload({ only: ["orders"] });

							// A change that left a payment row open has a next step, so hand
							// that step straight over instead of dropping the admin back on
							// the list with a flash message. Not a navigation: same page, same
							// filters, same scroll — one dialog closes and the settle actions
							// open.
							if (leavesPaymentOpen && globalProps?.selectedOrder) {
								openSettlePayments(String(globalProps.selectedOrder.id));
							}
						}}
					/>
				</ResponsiveDialog>
			)}

			{/* Change Voucher Dialog */}
			{globalProps?.selectedOrder && dialogChangeVoucher.isOpen && (
				<ResponsiveDialog {...dialogChangeVoucher}>
					<ChangeVoucherForm
						order={globalProps.selectedOrder}
						vouchers={globalProps.optionsData?.vouchers || []}
						onSuccess={(leavesPaymentOpen) => {
							router.reload({ only: ["orders"] });

							// Same handover as change-package: a discount change that left a
							// charge or refund open has a next step, so open it rather than
							// dropping the admin back on the list with a flash message.
							if (leavesPaymentOpen && globalProps?.selectedOrder) {
								openSettlePayments(String(globalProps.selectedOrder.id));
							}
						}}
					/>
				</ResponsiveDialog>
			)}

			{/* Settle Payments Dialog */}
			{globalProps?.selectedOrder && dialogSettlePayments.isOpen && (
				<ResponsiveDialog {...dialogSettlePayments}>
					<SettlePaymentsPanel
						order={globalProps.selectedOrder}
						onClose={() => dialogSettlePayments.onOpenChange?.(false)}
					/>
				</ResponsiveDialog>
			)}

			{/* Regenerate Invoice Dialog */}
			{globalProps?.selectedOrder && dialogRegenerateInvoice.isOpen && (
				<ResponsiveDialog {...dialogRegenerateInvoice}>
					<RegenerateInvoiceForm order={globalProps.selectedOrder} />
				</ResponsiveDialog>
			)}

			{/* Update Address Dialog */}
			{globalProps?.selectedAppointment && dialogUpdateAddress.isOpen && (
				<ResponsiveDialog {...dialogUpdateAddress}>
					<UpdateAddressHistoryForm
						selectedAppointment={globalProps.selectedAppointment}
						locations={globalProps.optionsData?.locations || []}
						pageURL={pageURL}
						refreshOnly={[
							"adminPortal",
							"flash",
							"errors",
							"selectedAppointment",
							"orders",
						]}
					/>
				</ResponsiveDialog>
			)}

			{/* Update Medical Record Dialog */}
			{globalProps?.selectedAppointment && dialogEditMedicalRecord.isOpen && (
				<ResponsiveDialog {...dialogEditMedicalRecord}>
					<EditMedicalRecordForm
						key={globalProps.selectedAppointment.id}
						selectedAppointment={globalProps.selectedAppointment}
						pageURL={pageURL}
						refreshOnly={[
							"adminPortal",
							"flash",
							"errors",
							"selectedAppointment",
							"orders",
						]}
					/>
				</ResponsiveDialog>
			)}

			{/* Update SOAP Dialog */}
			{globalProps?.selectedAppointment && dialogEditSoap.isOpen && (
				<ResponsiveDialog {...dialogEditSoap}>
					<EditSoapForm
						key={globalProps.selectedAppointment.id}
						selectedAppointment={globalProps.selectedAppointment}
						pageURL={pageURL}
						refreshOnly={[
							"adminPortal",
							"flash",
							"errors",
							"selectedAppointment",
							"orders",
						]}
					/>
				</ResponsiveDialog>
			)}

			{/* Feedback Reminder Dialog */}
			{feedbackDialog.isOpen && (
				<FeedbackReminderDialog
					isOpen={feedbackDialog.isOpen}
					onOpenChange={handleCloseFeedbackDialog}
					registrationNumber={feedbackDialog.registrationNumber}
					orderId={feedbackDialog.orderId}
				/>
			)}
		</>
	);
}
